import { createSelector } from '@reduxjs/toolkit'

import { TComment, TCommentApi, TCommentThread } from 'types/GlobalComment'

import { ApplicationState } from 'redux/Stores/types'
import { selectAccount } from './Accounts'
import { selectKeyMetric } from './KeyMetrics'
import { selectReportWidget } from './Reports'
import { CommentReducerType } from 'redux/reducers/Comments'

const EMPTY_THREAD: TCommentThread = {
  comments: [],
  data: {
    datasetLabel: '',
    label: '',
    value: 0
  },
  id: '',
  widgetId: null
}

const selectCommentStore = (state: ApplicationState) => state.CommentStore

function toTComment(comment: TCommentApi, state: ApplicationState): TComment {
  return {
    id: comment.id,
    created_at: comment.created_at,
    title: comment.comment,
    account: selectAccount(state, comment.created_by),
    is_homepal_author: comment.is_homepal_author,
    auto: comment.auto
  }
}

const getReplies = (commentStore: CommentReducerType, rootId: string) => {
  return Object.values(commentStore.data)
    .filter((c) => c.parent_id === rootId)
    .sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )
}

const createThread = (
  root: TCommentApi,
  state: ApplicationState,
  commentStore: CommentReducerType
): TCommentThread => {
  const replies = getReplies(commentStore, root.id)

  let datasetLabel = ''

  if (root.key_metric_id) {
    datasetLabel =
      selectKeyMetric(state, root.key_metric_id ?? undefined)?.title ?? ''
  } else if (root.widget_id) {
    const widget = selectReportWidget(state, root.widget_id)

    datasetLabel = (widget && 'title' in widget ? widget.title : null) ?? ''
  }

  return {
    comments: [root, ...replies].map((c) => toTComment(c, state)),
    data: {
      datasetLabel,
      label: root.segment ?? '',
      value: parseFloat(root.segment_value ?? '') || 0
    },
    id: root.id,
    widgetId: root.widget_id
  }
}

export const selectCommentThreadsForKeyMetric = createSelector(
  [
    (_state: ApplicationState) => _state,
    selectCommentStore,
    (_state: ApplicationState, keyMetricId: string | undefined) => keyMetricId
  ],
  (state, commentStore, keyMetricId): TCommentThread[] => {
    if (!keyMetricId) {
      return []
    }

    const rootIds = commentStore.byKeyMetricId[keyMetricId] ?? []

    return rootIds.reduce<TCommentThread[]>((acc, rootId) => {
      const root = commentStore.data[rootId]

      if (!root) {
        return acc
      }

      acc.push(createThread(root, state, commentStore))

      return acc
    }, [])
  }
)

export const selectCommentThreadsForReportEdition = createSelector(
  [
    (_state: ApplicationState) => _state,
    selectCommentStore,
    (_state: ApplicationState, reportEditionId: string | undefined) =>
      reportEditionId
  ],
  (state, commentStore, reportEditionId): TCommentThread[] => {
    if (!reportEditionId) {
      return []
    }

    const rootIds = commentStore.byReportEditionId[reportEditionId] ?? []

    return rootIds.reduce<TCommentThread[]>((acc, rootId) => {
      const root = commentStore.data[rootId]

      if (!root) {
        return acc
      }

      acc.push(createThread(root, state, commentStore))

      return acc
    }, [])
  }
)

export const selectCommentThreadsForAnalysis = createSelector(
  [
    (_state: ApplicationState) => _state,
    selectCommentStore,
    (_state: ApplicationState, analysisId: string | undefined) => analysisId
  ],
  (state, commentStore, analysisId): TCommentThread[] => {
    if (!analysisId) {
      return []
    }

    const roots = (commentStore.byAnalysisId[analysisId] ?? [])
      .map((rootId) => commentStore.data[rootId])
      .filter((root): root is TCommentApi => Boolean(root))
      .sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )

    return roots.map((root) => createThread(root, state, commentStore))
  }
)

export const selectCommentThread = createSelector(
  [
    (_state: ApplicationState) => _state,
    selectCommentStore,
    (_state: ApplicationState, id?: string | undefined) => id
  ],
  (state, commentStore, id): TCommentThread => {
    if (!id || !commentStore.data[id]) {
      return EMPTY_THREAD
    }

    const root = commentStore.data[id]

    return createThread(root, state, commentStore)
  }
)
